import { useParams } from "react-router-dom";
import {
  useGetSecurityPipelineWorkflowsQuery,
  useLoadOrganizationForSpaceQuery,
} from "~/operations";
import { useEffect, useState, useRef, useCallback } from "react";
import { ANIMATION_DELAYS } from "~/pages/integrations/security-pipeline/animation";
import { SecurityPipelineGithubWorkflowsTableRow } from "../types";
import { FormatRelativeDate } from "~/lib/date";
import { getWorkflowStatusCellProgress } from "../utils/statuses";
import { usePaginate } from "~/components/pagination/usePaginate";
import { useScope } from "~/hooks/useScope";
import { getIntegrationMrn } from "~/pages/integrations/utils/integrationLinksUtils";
import { useSort } from "~/pages/inventory/hooks/useSort";
import {
  OrderDirection,
  SecurityPipelineWorkflowOrder,
  SecurityPipelineWorkflowOrderField,
} from "~/operations/types";

interface UseSecurityPipelineGithubOverviewTabParams {
  scopeMrn: string;
}

export function useSecurityPipelineGithubOverviewTab({
  scopeMrn,
}: UseSecurityPipelineGithubOverviewTabParams) {
  const { integrationId } = useParams();
  const { activeScope, spaceScope } = useScope();
  const [showParticles, setShowParticles] = useState<boolean>(false);
  const [animationKey, setAnimationKey] = useState<number>(0);
  const numberRef = useRef<HTMLDivElement>(null);
  const paginate = usePaginate({ defaultPageSize: 50 });
  const { data: orgDataForSpace } = useLoadOrganizationForSpaceQuery({
    variables: { spaceMrn: spaceScope?.mrn ?? "" },
    skip: !spaceScope?.mrn,
  });
  const organizationName = orgDataForSpace?.space?.organization?.name;
  const { handleSortClick, orderBy } = useSort<SecurityPipelineWorkflowOrder>({
    defaultSort: {
      field: SecurityPipelineWorkflowOrderField.UpdatedAt,
      direction: OrderDirection.Desc,
    },
    validFields: [
      SecurityPipelineWorkflowOrderField.UpdatedAt,
      SecurityPipelineWorkflowOrderField.Title,
    ],
  });
  const {
    data,
    loading: getSecurityPipelineWorkflowsLoading,
    error: getSecurityPipelineWorkflowsError,
  } = useGetSecurityPipelineWorkflowsQuery({
    variables: {
      ...paginate.pageVars,
      orderBy,
      input: {
        scopeMrn,
        ...(activeScope
          ? {
              integrationMrn: getIntegrationMrn({
                scope: activeScope,
                integrationId,
              }),
            }
          : {}),
      },
    },
    // TODO: remove after testing
    fetchPolicy: "network-only",
  });

  const workflows =
    data?.securityPipelineWorkflows.__typename ===
    "SecurityPipelineWorkflowConnection"
      ? (data.securityPipelineWorkflows?.edges?.map((edge) => edge.node) ?? [])
      : [];

  const workflowsRows: SecurityPipelineGithubWorkflowsTableRow[] =
    workflows.map((workflow) => ({
      mrn: workflow.mrn,
      taskName: workflow.title,
      author: workflow.createdBy?.name,
      progress: getWorkflowStatusCellProgress(workflow.status),
      lastUpdated: FormatRelativeDate(workflow.updatedAt),
      status: workflow.status,
      ...(workflow.pullRequest?.url
        ? { pullRequestUrl: workflow.pullRequest.url }
        : {}),
    }));

  const paginationProps = paginate.pageProps(
    data?.securityPipelineWorkflows?.pageInfo,
  );

  useEffect(() => {
    if (!integrationId) return;
    setShowParticles(false);
    const timer = setTimeout(
      () => setShowParticles(true),
      ANIMATION_DELAYS.particles * 1000,
    );
    return () => clearTimeout(timer);
  }, [animationKey, integrationId]);

  const handleReplay = useCallback(() => {
    setAnimationKey((prev) => prev + 1);
    setShowParticles(false);
    // Start particles after the badge appears
    setTimeout(() => setShowParticles(true), ANIMATION_DELAYS.particles * 1000);
  }, []);

  const handleStatusClick = (row: SecurityPipelineGithubWorkflowsTableRow) => {
    if (row.pullRequestUrl) {
      window.open(row.pullRequestUrl, "_blank");
    }
  };

  const isLoading = getSecurityPipelineWorkflowsLoading;
  const isEmpty = workflows.length === 0 || (!workflows && !isLoading);

  return {
    organizationName,
    workflowsRows,
    paginationProps,
    error: getSecurityPipelineWorkflowsError,
    animationKey,
    showParticles,
    numberRef,
    handleReplay,
    handleStatusClick,
    isEmpty,
    orderBy,
    isLoading,
    handleSortClick,
  };
}
